import React, { ChangeEvent, FocusEvent, useEffect, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { isFunction } from 'lodash';

interface PasswordFieldProp {
  label: string;
  value?: string;
  autofocus?: boolean;
  correct?: boolean;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}

function PasswordField(props: PasswordFieldProp) {
  const [value, setValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (props.value != null && value !== props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }

  function handleMouseDownPassword(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const newValue = event.target.value;
    setValue(newValue);
    props.onChange(newValue);
  }

  function handleBlur() {
    if (isFunction(props.onBlur)) {
      props.onBlur(value);
    }
  }

  return (
    <TextField
      label={props.label}
      type={showPassword ? 'text' : 'password'}
      variant="outlined"
      margin="dense"
      size="small"
      fullWidth
      autoFocus={props.autofocus}
      value={value}
      error={!!props.error}
      helperText={props.error}
      disabled={props.disabled}
      required={props.required}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              disabled={!value}
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
            >
              {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
            {props.correct && <CheckIcon style={{ color: 'green' }} />}
          </InputAdornment>
        )
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={props.onFocus}
    />
  );
}

export default PasswordField;
